'use client';

import { useRef, useEffect } from 'react';
import { Clock, Users, BadgeCheck, LifeBuoy } from 'lucide-react';

/* ==================================================================
   Intro content data (stats + features; fades out before the panel expand)
   ================================================================== */

const stats = [
  { Icon: Clock, value: '8+', label: 'Years of Experience' },
  { Icon: Users, value: '200+', label: 'Clients Served' },
  { Icon: BadgeCheck, value: '88%', label: 'Client Retention Rate' },
  { Icon: LifeBuoy, value: '24/7', label: 'Dedicated Support' },
];

const features = [
  {
    title: 'An AI-First Engineering Partner',
    desc: 'Our engineers work as an extension of your team, turning hard problems into AI-first systems that ship. We pair senior talent with AI agents to move from strategy to production faster, with measurable outcomes at every step.',
  },
  {
    title: 'Production-Grade, End to End',
    desc: 'From AI product engineering to cloud, DevOps, and design, our work holds to a single standard: secure, scalable, production-ready software, delivered without the wait.',
  },
];

/* ==================================================================
   Tech stack — one row per niche (revealed inside the dark space)
   Techs with `logo` get an image; otherwise a brand-colored dot.
   All logos live in /public/techstack/
   ================================================================== */

const techStacks = [
  {
    label: 'Frontend',
    techs: [
      { name: 'HTML5', logo: '/techstack/html5.png' },
      { name: 'CSS3', logo: '/techstack/css3.png' },
      { name: 'React', logo: '/techstack/react.png' },
      { name: 'Next.js', logo: '/techstack/nextjs.png' },
      { name: 'Redux', logo: '/techstack/redux.png' },
      { name: 'Tailwind CSS', logo: '/techstack/tailwind.png' },
      { name: 'TypeScript', logo: '/techstack/typescript.png' },
      { name: 'Vue.js', logo: '/techstack/vuejs.png' },
      { name: 'JavaScript', logo: '/techstack/javascript.png' },
      { name: 'Bootstrap', logo: '/techstack/bootstrap.png' },
      { name: 'GraphQL', logo: '/techstack/graphql.png' },
      { name: 'Sass', logo: '/techstack/sass.png' },
    ],
  },
  {
    label: 'Backend',
    techs: [
      { name: 'Node.js', logo: '/techstack/nodejs.png' },
      { name: 'Express.js', logo: '/techstack/express.png' },
      { name: 'MongoDB', logo: '/techstack/mongodb.png' },
      { name: 'MySQL', logo: '/techstack/mysql.png' },
      { name: 'PostgreSQL', logo: '/techstack/postgresql.png' },
      { name: 'Prisma', logo: '/techstack/prisma.png' },
      { name: 'GraphQL', logo: '/techstack/graphql.png' },
      { name: 'Next.js', logo: '/techstack/nextjs.png' },
      { name: 'Socket.IO', logo: '/techstack/socketio.png' },
      { name: 'Firebase', logo: '/techstack/firebase.png' },
      /* REST API has no canonical logo; falls back to brand-blue dot */
      { name: 'REST API', color: '#058BF0' },
    ],
  },
  {
    label: 'AI Development',
    techs: [
      { name: 'Python', logo: '/techstack/python.png' },
      { name: 'TensorFlow', logo: '/techstack/tensorflow.png' },
      { name: 'PyTorch', logo: '/techstack/pytorch.png' },
      { name: 'scikit-learn', logo: '/techstack/scikit-learn.png' },
      { name: 'LangChain', logo: '/techstack/langchain.png' },
      { name: 'Hugging Face', logo: '/techstack/huggingface.png' },
      { name: 'NumPy', logo: '/techstack/numpy.png' },
      { name: 'OpenCV', logo: '/techstack/opencv.png' },
      { name: 'Jupyter', logo: '/techstack/jupyter.png' },
      { name: 'Pandas', logo: '/techstack/pandas.png' },
    ],
  },
  {
    label: 'Cloud & DevOps',
    techs: [
      { name: 'Google Cloud', logo: '/techstack/gcp.png' },
      { name: 'DigitalOcean', logo: '/techstack/digitalocean.png' },
      { name: 'Docker', logo: '/techstack/docker.png' },
      { name: 'Terraform', logo: '/techstack/terraform.png' },
      { name: 'Ansible', logo: '/techstack/ansible.png' },
      { name: 'Jenkins', logo: '/techstack/jenkins.png' },
      { name: 'Prometheus', logo: '/techstack/prometheus.png' },
      { name: 'Grafana', logo: '/techstack/grafana.png' },
      { name: 'AWS', logo: '/techstack/aws.png' },
      { name: 'GitLab CI/CD', logo: '/techstack/gitlab.png' },
      { name: 'Cloudflare', logo: '/techstack/cloudflare.png' },
    ],
  },
  {
    label: 'AI Automation',
    techs: [
      { name: 'n8n', logo: '/techstack/n8n.png' },
      { name: 'Zapier', logo: '/techstack/zapier.png' },
      { name: 'Apache Airflow', logo: '/techstack/airflow.png' },
      { name: 'Postman', logo: '/techstack/postman.png' },
      { name: 'Make', logo: '/techstack/make.png' },
      { name: 'OpenAI', logo: '/techstack/openai.png' },
      { name: 'FastAPI', logo: '/techstack/fastapi.png' },
      { name: 'LangChain', logo: '/techstack/langchain.png' },
      { name: 'Hugging Face', logo: '/techstack/huggingface.png' },
    ],
  },
  {
    label: 'UI / UX Design',
    techs: [
      { name: 'Figma', logo: '/techstack/figma.png' },
      { name: 'Framer', logo: '/techstack/framer.png' },
      { name: 'Adobe XD', logo: '/techstack/adobexd.png' },
      { name: 'Illustrator', logo: '/techstack/illustrator.png' },
      { name: 'Photoshop', logo: '/techstack/photoshop.png' },
      { name: 'Canva', logo: '/techstack/canva.png' },
    ],
  },
  {
    label: 'Project Management & Communication',
    techs: [
      { name: 'Jira', logo: '/techstack/jira.png' },
      { name: 'Trello', logo: '/techstack/trello.png' },
      { name: 'Asana', logo: '/techstack/asana.png' },
      { name: 'ClickUp', logo: '/techstack/clickup.png' },
      { name: 'Slack', logo: '/techstack/slack.png' },
      { name: 'Teams', logo: '/techstack/teams.png' },
      { name: 'Notion', logo: '/techstack/notion.png' },
      { name: 'Confluence', logo: '/techstack/confluence.png' },
    ],
  },
];

/* ==================================================================
   Marquee row — pure CSS keyframe (`tech-marquee`), duplicated enough
   times that translateX(-50%) loops seamlessly. Same recipe as the
   standalone TechCapabilities section.
   ================================================================== */

function TechRow({ techs, reverse, duration }) {
  const PILL_WIDTH_PX = 160; // rough avg incl. gap
  const TARGET_TRACK_WIDTH_PX = 6000; // 2 * 3000 (covers wide monitors)
  const minCopies = Math.ceil(
    TARGET_TRACK_WIDTH_PX / Math.max(1, techs.length * PILL_WIDTH_PX)
  );
  const evenMin = minCopies % 2 === 0 ? minCopies : minCopies + 1;
  const copies = Math.max(4, evenMin);
  const items = Array.from({ length: copies }, () => techs).flat();

  return (
    <div
      className="tech-row-host relative overflow-hidden"
      style={{ '--tech-marquee-duration': `${duration}s` }}
    >
      {/* Fade-out gradients on both edges */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 bg-gradient-to-r from-[#e9f1fa] to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 bg-gradient-to-l from-[#e9f1fa] to-transparent"
      />

      <div
        className={`tech-marquee-track flex gap-3 whitespace-nowrap ${reverse ? 'reverse' : ''}`}
      >
        {items.map((t, i) => (
          <div
            key={i}
            className="tech-pill tech-pill--light group inline-flex shrink-0 items-center gap-2.5 rounded-full border border-[#0a1b3a]/8 bg-white px-5 py-2.5 text-sm font-medium shadow-[0_1px_2px_rgba(10,27,58,0.05)] transition hover:border-[#058BF0]/40 hover:shadow-[0_8px_20px_-10px_rgba(5,139,240,0.45)]"
          >
            {t.logo ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={t.logo}
                alt={`${t.name} logo`}
                loading="lazy"
                decoding="async"
                className="h-5 w-5 shrink-0 object-contain"
              />
            ) : (
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: t.color, boxShadow: `0 0 8px ${t.color}80` }}
              />
            )}
            <span>{t.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==================================================================
   Sticky scrollytelling section
   The intro (stats + features) fades out, a blue-tinted panel expands from
   the connector dot between the feature cards, and the Tech Capabilities
   marquee reveals inside that panel, row by row.
   ================================================================== */

export default function Solutions() {
  const outerRef = useRef(null);
  const stickyRef = useRef(null);
  const contentRef = useRef(null);
  const contentInnerRef = useRef(null);
  const overlayRef = useRef(null);
  const headingRef = useRef(null);
  const circleRef = useRef(null);
  const rowsRef = useRef([]);

  useEffect(() => {
    let raf;
    const clamp = (v, lo = 0, hi = 1) => Math.min(Math.max(v, lo), hi);

    const apply = () => {
      const outer = outerRef.current;
      const sticky = stickyRef.current;
      if (!outer || !sticky) return;

      // Below lg the section renders as a normal static stack (CSS override in
      // hero-home.css); skip the scroll-driven circle-expand animation there.
      if (window.innerWidth < 1024) return;

      const rect = outer.getBoundingClientRect();
      const outerH = outer.offsetHeight;
      const innerH = sticky.offsetHeight;
      const p = clamp(-rect.top / Math.max(outerH - innerH, 1));

      // Phase 1: light content fades out (0.25 → 0.45)
      const fadeP = clamp((p - 0.25) / 0.2);
      if (contentRef.current) {
        contentRef.current.style.opacity = String(1 - fadeP);
        contentRef.current.style.filter = `blur(${fadeP * 12}px)`;
      }

      // On narrow phones the intro (heading + stat grid + feature cards) is
      // taller than one screen. Pan it from top to bottom over the first 20%
      // of scroll, before the fade-to-panel transition starts at 25%, so
      // none of it is permanently clipped by the sticky h-screen viewport
      // (same fix as the hero services grid).
      const contentInner = contentInnerRef.current;
      if (contentInner) {
        const vh = window.innerHeight;
        const h = contentInner.offsetHeight;
        const TOP_PAD = 96;
        const BOTTOM_PAD = 32;
        if (h + TOP_PAD + BOTTOM_PAD > vh) {
          const panP = clamp(p / 0.2);
          const naturalTop = (vh - h) / 2;
          const startTop = TOP_PAD;
          const endTop = vh - BOTTOM_PAD - h;
          const top = startTop + (endTop - startTop) * panP;
          contentInner.style.transform = `translate3d(0, ${top - naturalTop}px, 0)`;
        } else {
          contentInner.style.transform = '';
        }
      }

      // Phase 2: dark circle expands from the connector circle (0.30 → 0.55)
      const expandP = clamp((p - 0.3) / 0.25);
      if (overlayRef.current) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let cx = vw / 2;
        let cy = vh / 2;
        if (circleRef.current) {
          const r = circleRef.current.getBoundingClientRect();
          cx = r.left + r.width / 2;
          cy = r.top + r.height / 2;
        }
        const maxR = Math.max(
          Math.hypot(cx, cy),
          Math.hypot(vw - cx, cy),
          Math.hypot(cx, vh - cy),
          Math.hypot(vw - cx, vh - cy)
        );
        const r = 12 + expandP * (maxR + 40);
        overlayRef.current.style.clipPath = `circle(${r}px at ${cx}px ${cy}px)`;
      }

      // Phase 3a: Tech heading slides in (0.55 → 0.66)
      const headingP = clamp((p - 0.55) / 0.11);
      if (headingRef.current) {
        headingRef.current.style.opacity = String(headingP);
        headingRef.current.style.transform = `translateY(${(1 - headingP) * 60}px)`;
        headingRef.current.style.filter = `blur(${(1 - headingP) * 14}px)`;
      }

      // Phase 3b: Marquee rows reveal one-by-one, sliding up with blur clearing
      const ROW_START = 0.63;
      const ROW_SPACING = 0.035;
      const REVEAL_WINDOW = 0.05;
      rowsRef.current.forEach((row, i) => {
        if (!row) return;
        const rowStart = ROW_START + i * ROW_SPACING;
        const rowP = clamp((p - rowStart) / REVEAL_WINDOW);
        row.style.opacity = String(rowP);
        row.style.transform = `translateY(${(1 - rowP) * 40}px)`;
        row.style.filter = `blur(${(1 - rowP) * 8}px)`;
      });

      // Both the intro and the reveal panel are light surfaces, so the nav
      // keeps its light theme across the whole section — nothing to flip.
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={outerRef}
      data-nav-theme="light"
      className="solutions relative h-[450vh] bg-white"
    >
      <div
        ref={stickyRef}
        className="solutions-pin sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* Intro content (fades out) */}
        <div
          ref={contentRef}
          className="solutions-content absolute inset-0 flex items-center"
          style={{ willChange: 'opacity, filter' }}
        >
          <div ref={contentInnerRef} className="mx-auto w-11/12 max-w-[1280px] will-change-transform">
            <div className="text-center">
              <h2 className="mx-auto max-w-5xl font-poppins text-[28px] font-bold leading-[1.12] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl xl:text-[3.25rem]">
                AI-first solutions, built around your business
              </h2>
              <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-gray-600 lg:text-lg">
                Production-grade AI systems engineered to fit your workflows, your data, and your goals.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
              {stats.map(({ Icon, value, label }) => (
                <div
                  key={label}
                  className="bento-card group flex flex-col rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(10,27,58,0.04)] transition hover:-translate-y-0.5 hover:border-[#058BF0]/40 hover:shadow-lg hover:shadow-cloud/5 lg:p-6"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#058BF0] to-[#19418c] text-white shadow-[0_10px_24px_-8px_rgba(5,139,240,0.55)]">
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </div>
                  <p className="mt-4 font-poppins text-2xl font-extrabold leading-none tracking-tight text-[#058BF0] lg:text-3xl">
                    {value}
                  </p>
                  <p className="mt-2 text-xs font-semibold leading-snug text-[#0a1b3a] lg:text-sm">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            <div className="relative mt-5 grid auto-rows-fr gap-5 md:grid-cols-2 md:gap-16">
              {/* Connector line + circle (the dark-expand origin) */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-1/2 z-0 hidden -translate-y-1/2 items-center md:flex"
              >
                <svg className="block flex-1" height="3" preserveAspectRatio="none">
                  <line x1="0" y1="1.5" x2="100%" y2="1.5" stroke="#058BF0" strokeWidth="2" strokeDasharray="6 6" strokeLinecap="round" />
                </svg>
                <div ref={circleRef} className="mx-2 h-6 w-6 rounded-full bg-[#058BF0]" />
                <svg className="block flex-1" height="3" preserveAspectRatio="none">
                  <line x1="0" y1="1.5" x2="100%" y2="1.5" stroke="#058BF0" strokeWidth="2" strokeDasharray="6 6" strokeLinecap="round" />
                </svg>
              </div>

              {features.map(f => (
                <article
                  key={f.title}
                  className="group relative z-10 flex h-full flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-[#eaf2fb] via-[#e0ecf7] to-[#cee0f1] p-7 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-cloud/10 lg:p-9"
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-[#058BF0]/15 to-[#19418c]/5 blur-2xl"
                  />
                  <div className="relative">
                    <h3 className="font-poppins text-xl font-bold leading-tight text-[#0a1b3a] lg:text-2xl">
                      {f.title}
                    </h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-[#0a1b3a]/70 lg:text-base">
                      {f.desc}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {/* Expanding panel with Tech Capabilities */}
        <div
          ref={overlayRef}
          className="solutions-overlay absolute inset-0 bg-gradient-to-b from-[#eef5fc] via-[#e9f1fa] to-[#dfebf8]"
          style={{
            clipPath: 'circle(12px at 50% 50%)',
            willChange: 'clip-path',
          }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 h-[700px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#058BF0]/20 blur-[140px]"
          />

          {/* Tech capabilities layout */}
          <div className="solutions-tech absolute inset-0 flex flex-col items-center justify-center gap-8 lg:gap-10">
            {/* Heading */}
            <div
              ref={headingRef}
              className="solutions-tech-head px-4 text-center"
              style={{
                opacity: 0,
                transform: 'translateY(60px)',
                filter: 'blur(14px)',
                willChange: 'opacity, transform, filter',
              }}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#058BF0] sm:text-sm">
                Tech Stack
              </p>
              <h2 className="mx-auto mt-4 max-w-4xl font-poppins text-3xl font-bold leading-[1.1] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl">
                Our Tech Capabilities
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-gray-600 lg:text-base">
                The tools, frameworks and platforms we use to engineer AI-first systems across every layer of modern software.
              </p>
            </div>

            {/* Marquee rows */}
            <div className="flex w-full flex-col gap-3">
              {techStacks.map((stack, i) => (
                <div
                  key={stack.label}
                  ref={el => (rowsRef.current[i] = el)}
                  className="solutions-row"
                  style={{
                    opacity: 0,
                    transform: 'translateY(40px)',
                    filter: 'blur(8px)',
                    willChange: 'opacity, transform, filter',
                  }}
                >
                  <TechRow
                    techs={stack.techs}
                    reverse={i % 2 === 1}
                    duration={90 + (i % 3) * 16}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
